import { useEffect, useMemo, useRef, type Dispatch, type SetStateAction } from 'react';
import { ID_PROOF_RULES } from '../api/queries/auth';
import { useT } from '../i18n';
import { isValidName } from './name';
import { isValidPhone } from './phone';

/**
 * The field checks the forms share, in the reader's language.
 *
 * Each returns the message to show, or null when the entry is fine — which is
 * what lets a form clear its error the moment the entry becomes correct rather
 * than making the visitor submit again to find out.
 *
 * They are deliberately the same rules the server enforces, not stricter ones:
 * this saves a round trip, it does not decide anything the server has not
 * already decided.
 */
export const PASSWORD_MIN = 6;

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export function useValidators() {
  const { t } = useT();

  return useMemo(
    () => ({
      required: (value: string, label: string): string | null =>
        value.trim() ? null : t('valRequired', { label }),

      /**
       * A name field: required, and made only of the characters a name may hold.
       * Covers people, venues and assets — the server applies one rule to all
       * three, so the forms do too.
       */
      name: (value: string, label: string): string | null => {
        if (!value.trim()) return t('valRequired', { label });
        return isValidName(value) ? null : t('valNameChars', { label });
      },

      phone: (value: string): string | null => {
        const v = value.trim();
        if (!v) return t('valMobileRequired');
        return isValidPhone(v) ? null : t('valPhoneDigits');
      },

      /** Email is optional everywhere it appears, so blank passes. */
      optionalEmail: (value: string): string | null => {
        const v = value.trim();
        if (!v) return null;
        return EMAIL_RE.test(v) ? null : t('valEmailInvalid');
      },

      password: (value: string, min = PASSWORD_MIN): string | null => {
        if (!value) return t('valPasswordRequired');
        return value.length < min ? t('valPasswordMin', { count: min }) : null;
      },

      /**
       * A government ID number, checked for length only.
       *
       * Shape is deliberately not checked: a driving licence has no single
       * national format, and refusing a real document because it did not match
       * a guessed pattern is worse than accepting an odd-looking one — an admin
       * reviews every number against the uploaded scan before approving.
       */
      idNumber: (value: string, type: string, label: string): string | null => {
        const v = value.trim();
        if (!v) return t('valRequired', { label });
        const rule = ID_PROOF_RULES[type];
        if (!rule) return null;
        if (rule.exact && v.length !== rule.exact) {
          return rule.digits
            ? t('valIdDigits', { label, count: rule.exact })
            : t('valIdChars', { label, count: rule.exact });
        }
        return null;
      },
    }),
    [t],
  );
}

/**
 * Keep the messages already on screen in the reader's language.
 *
 * A form stores each message as text, written in the language of the moment it
 * was found, so switching language left "Mobile number is required" in English
 * under a Malayalam label until the next submit. When the language changes,
 * this re-runs the form's checks and swaps in the fresh text — only for fields
 * already showing a message, so switching never flags a field nobody touched.
 *
 * `recheck` returns the form's current errors, keyed like its error state.
 */
export function useRecheckOnLanguage<E extends object>(
  setErrors: Dispatch<SetStateAction<E>>,
  recheck: () => Partial<Record<keyof E, unknown>>,
) {
  const { lang } = useT();
  const latest = useRef(recheck);
  latest.current = recheck;
  const shown = useRef(lang);

  useEffect(() => {
    if (shown.current === lang) return;
    shown.current = lang;
    const fresh = latest.current() as Record<string, unknown>;
    setErrors((prev) => {
      const next = { ...prev } as Record<string, unknown>;
      for (const [key, message] of Object.entries(prev)) if (message) next[key] = fresh[key] ?? undefined;
      return next as E;
    });
  }, [lang, setErrors]);
}

/**
 * After a failed submit, bring the first field flagged inside `root` into view
 * and put the cursor in it. Run on the next frame, once the error state has
 * painted the fields.
 */
export function focusFirstInvalid(root: ParentNode | null = document) {
  requestAnimationFrame(() => {
    const el = root?.querySelector<HTMLElement>('[aria-invalid="true"], .invalid');
    if (!el) return;
    el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    const field = el.matches('input, textarea, select, button') ? el : el.querySelector<HTMLElement>('input, textarea, select, button');
    field?.focus({ preventScroll: true });
  });
}
